import React from 'react';
import { ERROR_MESSAGES } from '../config/constants.js';

// Custom error classes
export class APIError extends Error {
  constructor(message, statusCode = 500, details = null) {
    super(message);
    this.name = 'APIError';
    this.statusCode = statusCode;
    this.details = details;
  }
}

export class ValidationError extends Error {
  constructor(message, field = null) {
    super(message);
    this.name = 'ValidationError';
    this.field = field;
  }
}

export class AuthError extends Error {
  constructor(message = 'Authentication required') {
    super(message);
    this.name = 'AuthError';
  }
}

export class RateLimitError extends Error {
  constructor(message = 'Rate limit exceeded', retryAfter = 60) {
    super(message);
    this.name = 'RateLimitError';
    this.retryAfter = retryAfter;
  }
}

// Error logging utility
export const logError = (error, context = {}) => {
  const errorInfo = {
    name: error.name,
    message: error.message,
    stack: error.stack,
    timestamp: new Date().toISOString(),
    context
  };
  
  // In development, log to console
  if (process.env.NODE_ENV === 'development') {
    console.error('Error occurred:', errorInfo);
  }
  
  // In production, you might want to send to error tracking service
  // Example: Sentry.captureException(error, { extra: context });
  
  return errorInfo;
};

// Client-side error handler
export const handleClientError = (error, showNotification = null) => {
  logError(error, { location: 'client' });
  
  // Determine user-friendly message
  let userMessage = ERROR_MESSAGES.GENERIC_ERROR;
  
  if (error instanceof ValidationError) {
    userMessage = error.message;
  } else if (error instanceof AuthError) {
    userMessage = ERROR_MESSAGES.UNAUTHORIZED;
  } else if (error instanceof RateLimitError) {
    userMessage = ERROR_MESSAGES.RATE_LIMIT_EXCEEDED;
  } else if (error.name === 'NetworkError' || error.message.includes('fetch')) {
    userMessage = ERROR_MESSAGES.NETWORK_ERROR;
  }
  
  // Show notification if callback provided
  if (showNotification && typeof showNotification === 'function') {
    showNotification(userMessage, 'error');
  }
  
  return userMessage;
};

// Server-side error handler
export const handleServerError = (error, res, context = {}) => {
  logError(error, { location: 'server', ...context });
  
  // Determine response based on error type
  if (error instanceof ValidationError) {
    return res.status(400).json({
      error: ERROR_MESSAGES.VALIDATION_ERROR,
      details: error.message,
      field: error.field
    });
  }
  
  if (error instanceof AuthError) {
    return res.status(401).json({
      error: ERROR_MESSAGES.UNAUTHORIZED
    });
  }
  
  if (error instanceof RateLimitError) {
    return res.status(429).json({
      error: ERROR_MESSAGES.RATE_LIMIT_EXCEEDED,
      retryAfter: error.retryAfter
    });
  }
  
  if (error instanceof APIError) {
    return res.status(error.statusCode).json({
      error: error.message,
      details: error.details
    });
  }
  
  // Generic server error
  return res.status(500).json({
    error: ERROR_MESSAGES.SERVER_ERROR
  });
};

// Input validation helpers
export const validateEmail = (email) => {
  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  return emailRegex.test(email);
};

export const validateRequired = (value, fieldName) => {
  if (!value || (typeof value === 'string' && value.trim().length === 0)) {
    throw new ValidationError(`${fieldName} is required`, fieldName);
  }
  return true;
};

export const validateLength = (value, fieldName, maxLength) => {
  if (value && value.length > maxLength) {
    throw new ValidationError(`${fieldName} must be less than ${maxLength} characters`, fieldName);
  }
  return true;
};

export const sanitizeString = (str) => {
  if (typeof str !== 'string') return '';
  return str.trim().replace(/[<>]/g, ''); // Basic XSS prevention
};

// Async error wrapper for API calls
export const asyncHandler = (fn) => {
  return (req, res, next) => {
    Promise.resolve(fn(req, res, next)).catch(next);
  };
};

// Error boundary helper for React components
export const createErrorBoundary = (Component, fallback = null) => {
  return class extends React.Component {
    constructor(props) {
      super(props);
      this.state = { hasError: false, error: null };
    }
    
    static getDerivedStateFromError(error) {
      return { hasError: true, error };
    }
    
    componentDidCatch(error, errorInfo) {
      logError(error, { component: Component.name, ...errorInfo });
    }
    
    render() {
      if (this.state.hasError) {
        return fallback || <div>Something went wrong.</div>;
      }
      
      return <Component {...this.props} />;
    }
  };
};
